<template>
  <div class="flex flex-col gap-4">
    <!-- Validation warnings (non-blocking) -->
    <div
      v-if="warnings.length > 0"
      class="rounded border border-amber-500/40 bg-amber-900/30 px-3 py-2 text-xs text-amber-300 space-y-1"
    >
      <div class="font-semibold text-amber-400">Heads up</div>
      <div v-for="(w, i) in warnings" :key="i" class="leading-snug">{{ w }}</div>
    </div>

    <!-- Used by -->
    <div class="bg-slate-700/40 rounded-lg border border-slate-600 p-3">
      <div class="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">Used by</div>
      <div v-if="usages.length === 0" class="text-xs text-slate-500">Not used by any widget</div>
      <div v-else class="flex flex-col gap-1">
        <button
          v-for="u in usages"
          :key="u.objectUuid"
          class="text-left text-xs text-cyan-300 hover:text-cyan-200 hover:bg-slate-600/40 rounded px-2 py-1 transition-colors"
          @click="$emit('navigate-to-widget', { objectUuid: u.objectUuid, pageUuid: u.pageUuid })"
        >
          {{ capitalize(u.obj) }} {{ u.haspid }}<span v-if="u.comment" class="text-slate-400"> — {{ u.comment }}</span>
        </button>
      </div>
    </div>

    <!-- Inspector + preview -->
    <DatasetInspector :dataset="dataset" :referenceCount="usages.length" />

    <!-- Designer-only properties -->
    <div class="bg-slate-700/40 rounded-lg border border-slate-600 p-3 space-y-3">
      <div class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Designer</div>

      <div>
        <form-label class="text-xs text-slate-400 mb-1">Name</form-label>
        <form-input
          class="w-full text-sm"
          :modelValue="dataset.name"
          placeholder="Dataset name"
          @input="update('name', $event.target.value)"
        />
      </div>

    </div>

    <!-- Firmware properties (what gets exported) -->
    <div class="bg-slate-700/40 rounded-lg border border-slate-600 p-3 space-y-3">
      <div class="text-xs font-semibold text-slate-300 uppercase tracking-wide">Firmware</div>

      <div>
        <form-label class="text-xs text-slate-400 mb-1">Runtime ID</form-label>
        <form-input
          class="w-24 text-sm"
          type="number"
          min="0"
          :modelValue="dataset.id"
          @input="update('id', parseInt($event.target.value) || 0)"
        />
      </div>

      <div class="grid grid-cols-2 gap-3 pt-1 border-t border-slate-600">
        <div>
          <form-label class="text-xs text-slate-400 mb-1">Unit</form-label>
          <form-input
            class="w-full text-sm"
            :modelValue="dataset.unit ?? ''"
            placeholder="e.g. kWh"
            @input="update('unit', $event.target.value)"
          />
        </div>
        <div>
          <form-label class="text-xs text-slate-400 mb-1">Type</form-label>
          <form-select
            class="w-full text-sm"
            :modelValue="dataset.type ?? 'other'"
            @change="updateType($event.target.value)"
          >
            <option v-for="opt in DATASET_TYPES" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </form-select>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3 pt-1 border-t border-slate-600">
        <div>
          <form-label class="text-xs text-slate-400 mb-1">History (hours)</form-label>
          <form-input
            class="w-full text-sm"
            type="number"
            min="0.1"
            max="168"
            step="0.5"
            :modelValue="dataset.history_hours ?? 24"
            @input="update('history_hours', parseFloat($event.target.value) || 24)"
          />
        </div>
        <div>
          <form-label class="text-xs text-slate-400 mb-1">Interval (min)</form-label>
          <form-input
            class="w-full text-sm"
            type="number"
            min="0.5"
            max="1440"
            step="0.5"
            :modelValue="intervalMinutes"
            @input="updateIntervalMinutes($event.target.value)"
          />
          <div class="text-xs text-slate-500 mt-0.5">{{ dataset.interval ?? 300 }} s (stored value)</div>
        </div>
      </div>
    </div>

    <!-- Series -->
    <div class="bg-slate-700/40 rounded-lg border border-slate-600 overflow-hidden">
      <div class="px-3 py-2 border-b border-slate-600 bg-slate-700/50 flex items-center justify-between">
        <span class="text-sm font-semibold text-white">
          Series <span class="text-slate-500 font-normal">({{ dataset.series.length }}/{{ MAX_SERIES }})</span>
        </span>
        <button
          class="text-cyan-400 hover:text-cyan-300 text-xs font-medium px-2 py-1 rounded hover:bg-slate-600 transition-colors disabled:text-slate-600 disabled:hover:bg-transparent disabled:cursor-not-allowed"
          :disabled="dataset.series.length >= MAX_SERIES"
          @click="addSeries"
        >+ Add</button>
      </div>

      <div v-if="dataset.series.length === 0" class="px-3 py-4 text-xs text-slate-500 text-center">
        No series yet — click Add to create one
      </div>
      <div v-else-if="dataset.series.length >= MAX_SERIES" class="px-3 py-2 text-xs text-amber-500/80 text-center border-b border-slate-700">
        Maximum of {{ MAX_SERIES }} series reached
      </div>

      <div
        v-for="(series, idx) in dataset.series"
        :key="idx"
        class="border-b border-slate-700 last:border-b-0"
      >
        <!-- Series header bar (click to expand) -->
        <div
          class="flex items-center gap-2 px-3 py-2 cursor-pointer select-none hover:bg-slate-700/30 transition-colors"
          @click="toggleExpanded(idx)"
        >
          <span
            class="inline-block w-3 h-3 rounded-sm border border-slate-500 flex-shrink-0"
            :style="{ background: series.color || '#4da6ff' }"
          ></span>
          <span class="flex-1 text-xs text-slate-200 truncate">
            {{ series.label || 'Unnamed series ' + (idx + 1) }}
          </span>
          <div class="flex items-center gap-1 ml-auto" @click.stop>
            <button
              v-if="idx > 0"
              class="text-slate-500 hover:text-slate-300 p-0.5 rounded hover:bg-slate-600 transition-colors"
              title="Move up"
              @click="moveSeries(idx, idx - 1)"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <button
              v-if="idx < dataset.series.length - 1"
              class="text-slate-500 hover:text-slate-300 p-0.5 rounded hover:bg-slate-600 transition-colors"
              title="Move down"
              @click="moveSeries(idx, idx + 1)"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              class="text-red-500 hover:text-red-400 p-0.5 rounded hover:bg-slate-600 transition-colors"
              title="Delete series"
              @click="removeSeries(idx)"
            >
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <svg
            class="w-3 h-3 text-slate-500 transition-transform flex-shrink-0"
            :class="expandedSeries.has(idx) ? 'rotate-90' : ''"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>

        <!-- Series body -->
        <div v-if="expandedSeries.has(idx)" class="px-3 pb-3 pt-1 space-y-2 bg-slate-800/20">
          <div>
            <form-label class="text-xs text-slate-400 mb-1">Label</form-label>
            <form-input
              class="w-full text-sm"
              :modelValue="series.label"
              placeholder="e.g. Woonkamer"
              @input="updateSeries(idx, 'label', $event.target.value)"
            />
          </div>
          <div>
            <form-label class="text-xs text-slate-400 mb-1">Entity</form-label>
            <EntitySelector
              :modelValue="series.entity"
              :settings="settings"
              placeholder="sensor.example"
              @update:modelValue="updateSeries(idx, 'entity', $event)"
            />
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <form-label class="text-xs text-slate-400 mb-1">Color</form-label>
              <div class="flex items-center gap-2">
                <input
                  type="color"
                  class="w-8 h-8 rounded cursor-pointer border border-slate-500 bg-transparent p-0.5"
                  :value="series.color || '#4da6ff'"
                  @input="updateSeries(idx, 'color', $event.target.value)"
                />
                <form-input
                  class="flex-1 text-sm font-mono"
                  :modelValue="series.color || '#4da6ff'"
                  placeholder="#4da6ff"
                  @input="updateSeries(idx, 'color', $event.target.value)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useDatasetStore, MAX_SERIES, DATASET_TYPES } from '../../stores/datasetStore.js'
import { findWidgetsUsingDataset, getDatasetWarnings } from '../../utils/datasetValidation.js'
import EntitySelector from '../hassbuilder/EntitySelector.vue'
import DatasetInspector from './DatasetInspector.vue'

const props = defineProps({
  dataset: { type: Object, required: true },
  settings: { type: Object, required: true },
  objectlayer: { type: Object, default: null },
})
defineEmits(['navigate-to-widget'])

const store = useDatasetStore()
const expandedSeries = ref(new Set())

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s

const usages = computed(() =>
  props.objectlayer ? findWidgetsUsingDataset(props.objectlayer, props.dataset.uuid) : []
)

const warnings = computed(() =>
  getDatasetWarnings(props.dataset, store.all)
)

function updateType(type) {
  const patch = { type }
  const typeDef = DATASET_TYPES.find(t => t.value === type)
  if (!props.dataset.unit) patch.unit = typeDef?.unit ?? ''
  store.updateDataset(props.dataset.uuid, patch)
}

// Interval is stored in seconds (firmware/wire format) but edited in minutes.
const intervalMinutes = computed(() => Math.round(((props.dataset.interval ?? 300) / 60) * 100) / 100)

function updateIntervalMinutes(value) {
  const minutes = parseFloat(value) || 5
  update('interval', Math.round(minutes * 60))
}

function toggleExpanded(idx) {
  if (expandedSeries.value.has(idx)) {
    expandedSeries.value.delete(idx)
  } else {
    expandedSeries.value.add(idx)
  }
}

function update(field, value) {
  store.updateDataset(props.dataset.uuid, { [field]: value })
}

function updateSeries(idx, field, value) {
  props.dataset.series[idx][field] = value
  store._persist()
}

function addSeries() {
  store.addSeries(props.dataset.uuid)
  const newIdx = props.dataset.series.length - 1
  expandedSeries.value.add(newIdx)
}

function removeSeries(idx) {
  expandedSeries.value.delete(idx)
  const updated = new Set()
  for (const i of expandedSeries.value) {
    updated.add(i > idx ? i - 1 : i)
  }
  expandedSeries.value = updated
  store.removeSeries(props.dataset.uuid, idx)
}

function moveSeries(fromIdx, toIdx) {
  store.moveSeries(props.dataset.uuid, fromIdx, toIdx)
  const wasFromExpanded = expandedSeries.value.has(fromIdx)
  const wasToExpanded = expandedSeries.value.has(toIdx)
  if (wasFromExpanded) expandedSeries.value.add(toIdx); else expandedSeries.value.delete(toIdx)
  if (wasToExpanded) expandedSeries.value.add(fromIdx); else expandedSeries.value.delete(fromIdx)
}
</script>
